import React, { useCallback, useMemo, useState } from "react";
import Box from "@mui/material/Box";
import Badge from "@mui/material/Badge";
import Collapse from "@mui/material/Collapse";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import BarChartIcon from "@mui/icons-material/BarChart";
import { useTranslation } from "react-i18next";
import { useMapsLibrary } from "@vis.gl/react-google-maps";
import { useAppDispatch, useAppSelector } from "../../../framework/customStore";
import {
    selectVisibleGeofences,
    setStopDurationThresholdMinutes,
} from "../../../store/geofenceSlice";
import {
    GeofenceDto,
    GeofenceShapeType,
    CircleShapeData,
    RectangleShapeData,
    PolygonShapeData,
    parseShapeData,
} from "../../../models/geofence/geofence";
import { VehiclePositionInfo } from "../../../models/vehicle/vehiclePosition";
import {
    WorkShiftTimeSlotItem,
    WorkShiftTimeSlotItemBasic,
} from "../../../models/workShitTimeSlot/workShiftTimeSlotItem";
import { getWorkTimeTypeColorCode } from "../../../models/workShitTimeSlot/workTimeType";

interface WorkTimeTypeHit {
    workTimeTypeName: string;
    workTimeTypeType: number | null;
    count: number;
    totalDurationMs: number;
    totalDistanceKm: number;
    stopCount: number;
}

interface GeofenceHit {
    geofenceId: string;
    name: string;
    entryCount: number;
    totalDurationMs: number;
    totalDistanceKm: number;
    stopCount: number;
    workTimeTypes: WorkTimeTypeHit[];
}

// Returns a checker function that is closed over parsed shape data and any
// Google Maps objects that are expensive to construct (e.g. Polygon). This way
// those objects are created once per geofence rather than once per GPS position.
const buildGeofenceChecker = (
    geofence: GeofenceDto
): ((lat: number, lng: number) => boolean) | null => {
    const shapeData = parseShapeData(geofence);
    if (!shapeData) return null;

    if (geofence.shapeType === GeofenceShapeType.Circle) {
        const data = shapeData as CircleShapeData;
        const center = new google.maps.LatLng(data.centerLat, data.centerLng);
        return (lat, lng) => {
            const point = new google.maps.LatLng(lat, lng);
            const dist = google.maps.geometry.spherical.computeDistanceBetween(
                point,
                center
            );
            return dist <= data.radiusMeters;
        };
    } else if (geofence.shapeType === GeofenceShapeType.Rectangle) {
        const data = shapeData as RectangleShapeData;
        return (lat, lng) =>
            lat >= data.southWestLat &&
            lat <= data.northEastLat &&
            lng >= data.southWestLng &&
            lng <= data.northEastLng;
    } else {
        const data = shapeData as PolygonShapeData;
        const polygon = new google.maps.Polygon({ paths: data.points });
        return (lat, lng) => {
            const point = new google.maps.LatLng(lat, lng);
            return google.maps.geometry.poly.containsLocation(point, polygon);
        };
    }
};

const getSlotKey = (
    slot: WorkShiftTimeSlotItem | WorkShiftTimeSlotItemBasic | undefined
): string => {
    if (!slot) return "__none__";
    return slot.workTimeTypeId ?? "__none__";
};

const getSlotTypeName = (
    slot: WorkShiftTimeSlotItem | WorkShiftTimeSlotItemBasic | undefined
): string => {
    if (!slot) return "";
    if ("workTimeTypeName" in slot) {
        return slot.workTimeTypeName ?? "";
    }
    return "";
};

const getSlotTypeType = (
    slot: WorkShiftTimeSlotItem | WorkShiftTimeSlotItemBasic | undefined
): number | null => {
    if (!slot) return null;
    if ("workTimeTypeType" in slot) {
        return slot.workTimeTypeType ?? null;
    }
    return null;
};

const computeGeofenceHits = (
    positions: VehiclePositionInfo[],
    geofence: GeofenceDto,
    timeSlots: (WorkShiftTimeSlotItem | WorkShiftTimeSlotItemBasic)[],
    stopThresholdMs: number
): GeofenceHit => {
    // Build the checker once per geofence (avoids re-creating Polygon objects per position)
    const isInsideGeofence = buildGeofenceChecker(geofence);

    // Step 1: Extract geofence time intervals, distances, and stop counts from GPS positions
    const intervals: {
        enterMs: number;
        exitMs: number;
        distanceKm: number;
        stopCount: number;
    }[] = [];
    let wasInside = false;
    let entryTime: number | null = null;
    let intervalDistance = 0;
    let prevInsidePos: VehiclePositionInfo | null = null;

    // Stop tracking state
    let stopCount = 0;
    let stopStartMs = 0;
    let isStopped = false;

    for (const pos of positions) {
        const ts = new Date(pos.timestamp).getTime();
        const isInside = isInsideGeofence
            ? isInsideGeofence(pos.latitude, pos.longitude)
            : false;

        // speed can be null at runtime despite the TS type; treat null/undefined as unknown
        const speedIsZero = pos.speed != null && pos.speed === 0;
        const speedIsPositive = pos.speed != null && pos.speed > 0;

        if (isInside && !wasInside) {
            entryTime = ts;
            intervalDistance = 0;
            prevInsidePos = pos;
            stopCount = 0;
            isStopped = false;

            if (speedIsZero) {
                isStopped = true;
                stopStartMs = ts;
            }
        } else if (isInside && wasInside && prevInsidePos) {
            const dist = google.maps.geometry.spherical.computeDistanceBetween(
                new google.maps.LatLng(
                    prevInsidePos.latitude,
                    prevInsidePos.longitude
                ),
                new google.maps.LatLng(pos.latitude, pos.longitude)
            );
            intervalDistance += dist / 1000;
            prevInsidePos = pos;

            if (speedIsZero && !isStopped) {
                isStopped = true;
                stopStartMs = ts;
            } else if (speedIsPositive && isStopped) {
                if (ts - stopStartMs >= stopThresholdMs) stopCount++;
                isStopped = false;
            }
            // null/undefined speed: keep current stop state unchanged
        } else if (!isInside && wasInside && entryTime !== null) {
            if (isStopped && ts - stopStartMs >= stopThresholdMs) stopCount++;
            intervals.push({
                enterMs: entryTime,
                exitMs: ts,
                distanceKm: intervalDistance,
                stopCount,
            });
            entryTime = null;
            intervalDistance = 0;
            prevInsidePos = null;
            stopCount = 0;
            isStopped = false;
        }
        wasInside = isInside;
    }

    // Still inside at end of route
    if (wasInside && entryTime !== null && positions.length > 0) {
        const lastTime = new Date(
            positions[positions.length - 1].timestamp
        ).getTime();
        // End of data with ongoing stop: always count it (not a traffic light)
        if (isStopped) stopCount++;
        intervals.push({
            enterMs: entryTime,
            exitMs: lastTime,
            distanceKm: intervalDistance,
            stopCount,
        });
    }

    if (intervals.length === 0) {
        return {
            geofenceId: geofence.geofenceId,
            name: geofence.name,
            entryCount: 0,
            totalDurationMs: 0,
            totalDistanceKm: 0,
            stopCount: 0,
            workTimeTypes: [],
        };
    }

    // Step 2: Compute total duration, distance, entry count, and stop count
    const entryCount = intervals.length;
    const totalDurationMs = intervals.reduce(
        (sum, iv) => sum + (iv.exitMs - iv.enterMs),
        0
    );
    const totalDistanceKm = intervals.reduce(
        (sum, iv) => sum + iv.distanceKm,
        0
    );
    const totalStopCount = intervals.reduce((sum, iv) => sum + iv.stopCount, 0);

    // Step 3: Intersect geofence intervals with work shift time slots
    const wttMap = new Map<string, WorkTimeTypeHit>();

    for (const interval of intervals) {
        const intervalDuration = interval.exitMs - interval.enterMs;

        // First pass: find overlapping slots and track which has the largest overlap
        let bestOverlapIndex = -1;
        let bestOverlap = 0;

        const overlaps: {
            key: string;
            slot: WorkShiftTimeSlotItem | WorkShiftTimeSlotItemBasic;
            overlapDuration: number;
            overlapDistanceKm: number;
        }[] = [];

        for (const slot of timeSlots) {
            const slotStart = slot.startDate
                ? new Date(slot.startDate).getTime()
                : null;
            const slotEnd = slot.endDate
                ? new Date(slot.endDate).getTime()
                : null;
            if (slotStart === null) continue;

            // Compute overlap between geofence interval and time slot
            const overlapStart = Math.max(interval.enterMs, slotStart);
            const overlapEnd =
                slotEnd !== null
                    ? Math.min(interval.exitMs, slotEnd)
                    : interval.exitMs; // open-ended slot extends to end

            if (overlapStart >= overlapEnd) continue;

            const key = getSlotKey(slot);
            if (key === "__none__") continue;

            const overlapDuration = overlapEnd - overlapStart;
            const overlapDistanceKm =
                intervalDuration > 0
                    ? interval.distanceKm * (overlapDuration / intervalDuration)
                    : 0;

            overlaps.push({ key, slot, overlapDuration, overlapDistanceKm });

            if (overlapDuration > bestOverlap) {
                bestOverlap = overlapDuration;
                bestOverlapIndex = overlaps.length - 1;
            }
        }

        // Second pass: add to map, attributing stops only to the single slot with the largest overlap
        for (let idx = 0; idx < overlaps.length; idx++) {
            const { key, slot, overlapDuration, overlapDistanceKm } =
                overlaps[idx];
            const stopsForSlot =
                idx === bestOverlapIndex ? interval.stopCount : 0;

            const existing = wttMap.get(key);
            if (existing) {
                existing.count++;
                existing.totalDurationMs += overlapDuration;
                existing.totalDistanceKm += overlapDistanceKm;
                existing.stopCount += stopsForSlot;
            } else {
                wttMap.set(key, {
                    workTimeTypeName: getSlotTypeName(slot),
                    workTimeTypeType: getSlotTypeType(slot),
                    count: 1,
                    totalDurationMs: overlapDuration,
                    totalDistanceKm: overlapDistanceKm,
                    stopCount: stopsForSlot,
                });
            }
        }
    }

    const workTimeTypes: WorkTimeTypeHit[] = Array.from(wttMap.values())
        .filter((v) => v.count > 0)
        .sort((a, b) => b.totalDurationMs - a.totalDurationMs);

    return {
        geofenceId: geofence.geofenceId,
        name: geofence.name,
        entryCount,
        totalDurationMs,
        totalDistanceKm,
        stopCount: totalStopCount,
        workTimeTypes,
    };
};

const formatDuration = (ms: number): string => {
    const totalSeconds = Math.round(ms / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    if (hours > 0) return `${hours}h ${minutes}min`;
    return `${minutes}min`;
};

const formatDistanceKm = (km: number): string => {
    if (km < 0.1) return "< 0.1 km";
    return `${km.toFixed(1)} km`;
};

export const GeofenceLegend = () => {
    const { t } = useTranslation();
    const [expanded, setExpanded] = useState(false);
    const geometryLib = useMapsLibrary("geometry");

    const dispatch = useAppDispatch();
    const vehicleRoute = useAppSelector((s) => s.monitorView.vehicleRoute);
    const showOnMap = useAppSelector((s) => s.geofence.showOnMap);
    const filteredGeofences = useAppSelector(selectVisibleGeofences);
    const stopThresholdMinutes = useAppSelector(
        (s) => s.geofence.stopDurationThresholdMinutes
    );
    const stopThresholdMs = useMemo(
        () => stopThresholdMinutes * 60 * 1000,
        [stopThresholdMinutes]
    );

    const handleThresholdChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const val = parseFloat(e.target.value);
            if (!isNaN(val) && val >= 0) {
                dispatch(setStopDurationThresholdMinutes(val));
            }
        },
        [dispatch]
    );

    const isVehicleShiftTabView = useAppSelector(
        (s) => s.monitorView.isVehicleShiftTabView
    );
    const vehicleShift = useAppSelector(
        (s) => s.monitorView.selectedVehicleShift
    );
    const workShift = useAppSelector((s) => s.monitorView.selectedWorkShift);
    const timeSlots = useMemo(
        () =>
            isVehicleShiftTabView
                ? (vehicleShift?.workShiftTimeSlots ?? [])
                : (workShift?.workShiftTimeSlots ?? []),
        [isVehicleShiftTabView, vehicleShift, workShift]
    );

    const allPositions = useMemo(() => {
        if (!vehicleRoute?.length) return [];
        return vehicleRoute.flatMap((seg) => seg.positions ?? []);
    }, [vehicleRoute]);

    // Build the checker once per geofence (avoids re-creating Polygon objects per position)
    const hits = useMemo((): GeofenceHit[] => {
        if (!allPositions.length || !filteredGeofences.length || !geometryLib) {
            return [];
        }
        return filteredGeofences
            .map((g) =>
                computeGeofenceHits(allPositions, g, timeSlots, stopThresholdMs)
            )
            .filter((h) => h.entryCount > 0);
    }, [
        allPositions,
        filteredGeofences,
        geometryLib,
        timeSlots,
        stopThresholdMs,
    ]);

    // Only render when geofences are shown on the map
    if (!showOnMap) return null;

    return (
        <Box
            sx={{
                mt: 0.5,
                mr: "10px",
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-end",
            }}
        >
            <Badge
                badgeContent={hits.length}
                color="primary"
                invisible={hits.length === 0}
            >
                <IconButton
                    onClick={() => setExpanded((e) => !e)}
                    size="small"
                    title={t("monitorView.geofence.legendTitle")}
                    sx={{
                        bgcolor: "background.paper",
                        borderRadius: 0.5,
                        boxShadow: 2,
                        width: 40,
                        height: 40,
                        "&:hover": { bgcolor: "grey.100" },
                    }}
                >
                    <BarChartIcon fontSize="small" />
                </IconButton>
            </Badge>
            <Collapse in={expanded} unmountOnExit>
                <Box
                    sx={{
                        bgcolor: "background.paper",
                        borderRadius: 1,
                        boxShadow: 3,
                        mt: 0.5,
                        px: 1.5,
                        py: 0.75,
                        whiteSpace: "nowrap",
                    }}
                >
                    <Typography
                        variant="subtitle2"
                        fontWeight="bold"
                        sx={{ display: "block", mb: 0.5 }}
                    >
                        {t("monitorView.geofence.legendTitle")}
                    </Typography>
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                            mb: 0.5,
                        }}
                    >
                        <Typography variant="caption" color="text.secondary">
                            {t("monitorView.geofence.stopThreshold")}
                        </Typography>
                        <TextField
                            id="geofence-stop-threshold"
                            type="number"
                            size="small"
                            value={stopThresholdMinutes}
                            onChange={handleThresholdChange}
                            inputProps={{
                                min: 0,
                                step: 0.5,
                                style: {
                                    padding: "2px 4px",
                                    width: 32,
                                    fontSize: "0.75rem",
                                    textAlign: "center",
                                },
                            }}
                            sx={{
                                "& .MuiOutlinedInput-root": {
                                    // Hide number spinner arrows
                                    "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button":
                                        {
                                            WebkitAppearance: "none",
                                            margin: 0,
                                        },
                                },
                            }}
                        />
                        <Typography variant="caption" color="text.secondary">
                            {t("monitorView.geofence.stopThresholdUnit")}
                        </Typography>
                    </Box>
                    <Divider sx={{ mb: 0.5 }} />
                    {hits.length === 0 ? (
                        <Typography
                            variant="caption"
                            color="text.disabled"
                            sx={{ display: "block" }}
                        >
                            {t("monitorView.geofence.legendNoHits")}
                        </Typography>
                    ) : (
                        <Box
                            component="table"
                            sx={{ width: "100%", borderCollapse: "collapse" }}
                        >
                            <Box component="thead">
                                <Box component="tr">
                                    <Box
                                        component="th"
                                        sx={{ textAlign: "left", pb: 0.25 }}
                                    >
                                        <Typography
                                            variant="caption"
                                            color="text.secondary"
                                        >
                                            {t(
                                                "monitorView.geofence.legendName"
                                            )}
                                        </Typography>
                                    </Box>
                                    <Box
                                        component="th"
                                        sx={{ textAlign: "right", pb: 0.25 }}
                                    >
                                        <Typography
                                            variant="caption"
                                            color="text.secondary"
                                        >
                                            {t(
                                                "monitorView.geofence.legendEntries"
                                            )}
                                        </Typography>
                                    </Box>
                                    <Box
                                        component="th"
                                        sx={{ textAlign: "right", pb: 0.25 }}
                                    >
                                        <Typography
                                            variant="caption"
                                            color="text.secondary"
                                        >
                                            {t(
                                                "monitorView.geofence.legendDuration"
                                            )}
                                        </Typography>
                                    </Box>
                                    <Box
                                        component="th"
                                        sx={{
                                            textAlign: "right",
                                            pb: 0.25,
                                            pl: 1,
                                        }}
                                    >
                                        <Typography
                                            variant="caption"
                                            color="text.secondary"
                                        >
                                            {t(
                                                "monitorView.geofence.legendDistance"
                                            )}
                                        </Typography>
                                    </Box>
                                    <Box
                                        component="th"
                                        sx={{
                                            textAlign: "right",
                                            pb: 0.25,
                                            pl: 1,
                                        }}
                                    >
                                        <Typography
                                            variant="caption"
                                            color="text.secondary"
                                        >
                                            {t(
                                                "monitorView.geofence.legendStops"
                                            )}
                                        </Typography>
                                    </Box>
                                </Box>
                            </Box>
                            <Box component="tbody">
                                {hits.map((hit) => (
                                    <React.Fragment key={hit.geofenceId}>
                                        <Box component="tr">
                                            <Box
                                                component="td"
                                                sx={{ py: 0.25, pr: 1 }}
                                            >
                                                <Typography
                                                    variant="caption"
                                                    fontWeight="bold"
                                                >
                                                    {hit.name}
                                                </Typography>
                                            </Box>
                                            <Box
                                                component="td"
                                                sx={{
                                                    textAlign: "right",
                                                    py: 0.25,
                                                }}
                                            >
                                                <Typography
                                                    variant="caption"
                                                    fontWeight="bold"
                                                >
                                                    {hit.entryCount}
                                                </Typography>
                                            </Box>
                                            <Box
                                                component="td"
                                                sx={{
                                                    textAlign: "right",
                                                    py: 0.25,
                                                    pl: 1,
                                                }}
                                            >
                                                <Typography
                                                    variant="caption"
                                                    fontWeight="bold"
                                                >
                                                    {formatDuration(
                                                        hit.totalDurationMs
                                                    )}
                                                </Typography>
                                            </Box>
                                            <Box
                                                component="td"
                                                sx={{
                                                    textAlign: "right",
                                                    py: 0.25,
                                                    pl: 1,
                                                }}
                                            >
                                                <Typography
                                                    variant="caption"
                                                    fontWeight="bold"
                                                >
                                                    {formatDistanceKm(
                                                        hit.totalDistanceKm
                                                    )}
                                                </Typography>
                                            </Box>
                                            <Box
                                                component="td"
                                                sx={{
                                                    textAlign: "right",
                                                    py: 0.25,
                                                    pl: 1,
                                                }}
                                            >
                                                <Typography
                                                    variant="caption"
                                                    fontWeight="bold"
                                                >
                                                    {hit.stopCount}
                                                </Typography>
                                            </Box>
                                        </Box>
                                        {hit.workTimeTypes.map((wtt, i) => (
                                            <Box
                                                component="tr"
                                                key={`${hit.geofenceId}-wtt-${i}`}
                                            >
                                                <Box
                                                    component="td"
                                                    sx={{
                                                        py: 0.125,
                                                        pr: 1,
                                                        pl: 1.5,
                                                    }}
                                                >
                                                    <Box
                                                        sx={{
                                                            display: "flex",
                                                            alignItems:
                                                                "center",
                                                            gap: 0.5,
                                                        }}
                                                    >
                                                        <Box
                                                            sx={{
                                                                width: 8,
                                                                height: 8,
                                                                borderRadius:
                                                                    "50%",
                                                                bgcolor:
                                                                    getWorkTimeTypeColorCode(
                                                                        wtt.workTimeTypeType
                                                                    ),
                                                                flexShrink: 0,
                                                            }}
                                                        />
                                                        <Typography
                                                            variant="caption"
                                                            color="text.secondary"
                                                            noWrap
                                                        >
                                                            {
                                                                wtt.workTimeTypeName
                                                            }
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                                <Box
                                                    component="td"
                                                    sx={{
                                                        textAlign: "right",
                                                        py: 0.125,
                                                    }}
                                                >
                                                    <Typography
                                                        variant="caption"
                                                        color="text.secondary"
                                                    >
                                                        {wtt.count}
                                                    </Typography>
                                                </Box>
                                                <Box
                                                    component="td"
                                                    sx={{
                                                        textAlign: "right",
                                                        py: 0.125,
                                                        pl: 1,
                                                    }}
                                                >
                                                    <Typography
                                                        variant="caption"
                                                        color="text.secondary"
                                                    >
                                                        {formatDuration(
                                                            wtt.totalDurationMs
                                                        )}
                                                    </Typography>
                                                </Box>
                                                <Box
                                                    component="td"
                                                    sx={{
                                                        textAlign: "right",
                                                        py: 0.125,
                                                        pl: 1,
                                                    }}
                                                >
                                                    <Typography
                                                        variant="caption"
                                                        color="text.secondary"
                                                    >
                                                        {formatDistanceKm(
                                                            wtt.totalDistanceKm
                                                        )}
                                                    </Typography>
                                                </Box>
                                                <Box
                                                    component="td"
                                                    sx={{
                                                        textAlign: "right",
                                                        py: 0.125,
                                                        pl: 1,
                                                    }}
                                                >
                                                    <Typography
                                                        variant="caption"
                                                        color="text.secondary"
                                                    >
                                                        {wtt.stopCount}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        ))}
                                    </React.Fragment>
                                ))}
                            </Box>
                        </Box>
                    )}
                </Box>
            </Collapse>
        </Box>
    );
};
